import React, { useState } from 'react';
import { Row } from 'react-bootstrap';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DateTimePicker } from '@/components/ui/shadcn/custom/DateTimePicker/date-time-picker';
import {
  ComboboxField,
  type ComboboxFieldOption,
} from 'components/DesignSystems/ComboboxField/ComboboxField';
import ActionStatusSelect from 'components/Actions/ActionStatusSelect';
import FormGeoLocationField from 'components/IAP/formFields/FormGeoLocationField';
import FormSymbologySelect from 'components/MapPage/CorporateViewTabs/LayersTab/FormSymbologySelect';
import { type GeoLocation } from 'components/GeoLocationComponent/GeoLocationComponent';
import ResourceRequirements, {
  ResourceRequirement,
} from 'components/IAP/ResourceRequirements';
import HazardsRisks, { HazardRisk } from 'components/IAP/HazardsRisks';
import ActionAttachments, {
  AttachmentFile,
  AttachmentLink,
} from 'components/Actions/ActionAttachments';
import FormCheckboxField from 'components/IAP/formFields/FormCheckboxField';
import ActionChecklist, {
  CHECKLIST_DONE_STATUS,
  createChecklistItem,
  type ChecklistItem,
} from 'components/Actions/ActionChecklist';
import { ORGANIZATION } from 'components/IAP/ICSConstants';
import {
  ActionFormValues,
  useScopedActionForm,
} from 'components/Actions/hooks/useScopedActionForm';
import {
  GetOrgActionResponse,
  useOrgActions,
} from 'components/Actions/hooks/orgActionHooks';
import { useAors } from 'hooks/useAors';

type FormFields = {
  name: string;
  poc?: string | null;
  briefed_poc?: boolean;
  status: string;
  start_date?: Date | string | null;
  target_date?: Date | string | null;
  actual_date?: Date | string | null;
  // Task-modal fields; mapped to/from the API shape by
  // `orgActionToFormFields` and `onSubmit`.
  priority?: string;
  actionDescription?: string;
  resourceRequirements?: ResourceRequirement[];
  hazardsRisks?: HazardRisk[];
  attachmentFiles?: AttachmentFile[];
  attachmentLinks?: AttachmentLink[];
  actionSymbology?: string;
  actionLocation?: GeoLocation;
  actionAors?: string[];
  blockedBy?: string[];
};

/** Maps a saved org action onto the modal's form field names. */
const orgActionToFormFields = (action: GetOrgActionResponse): FormFields => ({
  name: action.name,
  poc: action.poc,
  briefed_poc: action.briefedPoc,
  status: action.status ?? 'Planned',
  start_date: action.startDate,
  target_date: action.targetDate,
  actual_date: action.actualDate,
  priority: action.severity ?? 'normal',
  actionDescription: action.description ?? '',
  resourceRequirements: action.resourceRequirements ?? [],
  hazardsRisks: action.hazardsRisks ?? [],
  attachmentFiles: action.attachments?.files ?? [],
  attachmentLinks: action.attachments?.links ?? [],
  actionSymbology: action.symbol ?? '',
  actionLocation: action.location ?? undefined,
  actionAors: action.aors ?? [],
  blockedBy: action.blockedBy ?? [],
});

const PRIORITY_OPTIONS = [
  { label: 'Low', value: 'low' },
  { label: 'Normal', value: 'normal' },
  { label: 'High', value: 'high' },
  { label: 'Critical', value: 'critical' },
];

const FieldErrorText: React.FC<{ error?: { message?: string } }> = ({
  error,
}) =>
  error?.message ? (
    <p className="tw:text-sm tw:text-destructive">{error.message}</p>
  ) : null;

type ComboboxFormFieldProps = {
  title: string;
  fieldName: 'priority' | 'poc' | 'actionAors' | 'blockedBy';
  options: ComboboxFieldOption[];
  required?: string;
  multiple?: boolean;
};

const ComboboxFormField: React.FC<ComboboxFormFieldProps> = ({
  title,
  fieldName,
  options,
  required,
  multiple = false,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormFields>();

  return (
    <div className="tw:flex tw:flex-col tw:gap-1">
      <Controller
        control={control}
        name={fieldName}
        rules={required ? { required } : undefined}
        render={({ field: { value, onChange } }) => (
          <ComboboxField
            label={required ? `${title} *` : title}
            options={options}
            multiple={multiple}
            value={
              multiple
                ? Array.isArray(value)
                  ? (value as string[])
                  : []
                : typeof value === 'string' && value
                  ? value
                  : null
            }
            onChange={(next) =>
              onChange(
                multiple
                  ? Array.isArray(next)
                    ? next
                    : []
                  : typeof next === 'string'
                    ? next
                    : ''
              )
            }
          />
        )}
      />
      <FieldErrorText error={errors[fieldName]} />
    </div>
  );
};

const StatusFormField: React.FC = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormFields>();

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <Label htmlFor="status-select">Status *</Label>
      <Controller
        control={control}
        name="status"
        rules={{ required: 'Status is required' }}
        render={({ field: { value, onChange } }) => (
          <ActionStatusSelect
            id="status-select"
            className="tw:w-full"
            value={value}
            onChange={onChange}
          />
        )}
      />
      <FieldErrorText error={errors.status} />
    </div>
  );
};

type DateTimeFormFieldProps = {
  title: string;
  fieldName: 'start_date' | 'target_date' | 'actual_date';
};

const DateTimeFormField: React.FC<DateTimeFormFieldProps> = ({
  title,
  fieldName,
}) => {
  const { control } = useFormContext<FormFields>();
  const inputId = `${fieldName}-datetime`;

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <Label htmlFor={inputId}>{title}</Label>
      <Controller
        control={control}
        name={fieldName}
        render={({ field: { value, onChange } }) => {
          const parsed = value ? new Date(value) : undefined;
          return (
            <DateTimePicker
              id={inputId}
              className="tw:w-full"
              dateTime={
                parsed && !Number.isNaN(parsed.getTime()) ? parsed : undefined
              }
              onValueChange={(next) =>
                onChange(next ? next.toISOString() : null)
              }
            />
          );
        }}
      />
    </div>
  );
};

type EditOrgActionsModalProps = {
  /** When provided the modal edits this action; otherwise it adds a new one. */
  selectedEntity?: GetOrgActionResponse;
  /** `item_reference` id (e.g. a report) a newly added action is linked to. */
  linkedItemId?: UUID;
  toggle: () => void;
};

const EditOrgActionsModal: React.FC<EditOrgActionsModalProps> = ({
  selectedEntity,
  linkedItemId,
  toggle,
}) => {
  const isEdit = !!selectedEntity;
  const adapter = useScopedActionForm(ORGANIZATION);
  const { data: aors = [] } = useAors();
  const { data: orgActions = [] } = useOrgActions();

  const aorOptions = aors.map((aor) => ({ label: aor.name, value: aor.id }));
  const blockedByOptions = orgActions
    .filter((action) => action.actionId !== selectedEntity?.actionId)
    .map((action) => ({ label: action.name, value: action.actionId }));

  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: selectedEntity
      ? orgActionToFormFields(selectedEntity)
      : { status: 'Planned', priority: 'normal', resourceRequirements: [] },
  });
  const { handleSubmit, watch } = form;
  const watchedName = watch('name');

  const [checklist, setChecklist] = useState<ChecklistItem[]>(
    selectedEntity?.checklist?.map((item) =>
      createChecklistItem({
        label: item.label,
        status: item.checked ? CHECKLIST_DONE_STATUS : 'Planned',
      })
    ) ?? []
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const onSubmit = async (formData: FormFields) => {
    // Files are already uploaded to the org file library by the time the form
    // is submitted; only their metadata is stored on the action.
    const {
      priority,
      actionDescription,
      resourceRequirements,
      hazardsRisks,
      attachmentFiles,
      attachmentLinks,
      actionSymbology,
      actionLocation,
      actionAors,
      blockedBy,
      ...persistedData
    } = formData;
    const values: ActionFormValues = {
      ...persistedData,
      description: actionDescription ?? '',
      severity: priority || null,
      resourceRequirements: resourceRequirements ?? [],
      hazardsRisks: hazardsRisks ?? [],
      attachments: {
        files: attachmentFiles ?? [],
        links: (attachmentLinks ?? []).filter((link) => link.url.trim()),
      },
      symbol: actionSymbology || null,
      location: actionLocation ?? null,
      aors: actionAors ?? [],
      blockedBy: blockedBy ?? [],
      checklist: checklist
        .filter((item) => item.label.trim())
        .map((item) => ({
          label: item.label,
          checked: item.status === CHECKLIST_DONE_STATUS,
        })),
    };
    if (selectedEntity) {
      await adapter.update(selectedEntity, values);
    } else {
      await adapter.create(values, { itemId: linkedItemId });
    }
    toggle();
  };

  const toggleActive = async () => {
    if (selectedEntity) {
      await adapter.archive(selectedEntity, !selectedEntity.archived);
    }
    setShowConfirmModal(false);
    toggle();
  };

  const handleDeactivateClick = () => {
    if (!selectedEntity?.archived) {
      setShowConfirmModal(true);
    } else {
      void toggleActive();
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) toggle();
      }}
    >
      <DialogContent className="tw:h-[90vh] tw:grid-rows-[auto_minmax(0,1fr)] tw:overflow-hidden tw:sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Task' : 'Add Task'}</DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="tw:flex tw:min-h-0 tw:flex-col tw:gap-4"
          >
            <div className="tw:flex tw:min-h-0 tw:-mx-3 tw:flex-1 tw:flex-col tw:gap-4 tw:overflow-x-hidden tw:overflow-y-auto tw:px-3">
              <div className="tw:mt-2 tw:flex tw:flex-col tw:gap-2">
                <Label htmlFor="actionName-input">Name *</Label>
                <Input
                  id="actionName-input"
                  type="text"
                  aria-invalid={!!form.formState.errors.name}
                  {...form.register('name', {
                    required: 'This field is required',
                    maxLength: {
                      value: 250,
                      message: 'This maximum length is 250 characters',
                    },
                  })}
                />
                <FieldErrorText error={form.formState.errors.name} />
              </div>
              <div className="tw:grid tw:grid-cols-[minmax(0,1fr)] tw:items-start tw:gap-4 tw:md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <div>
                  <div className="tw:mt-2 tw:grid tw:grid-cols-2 tw:gap-3">
                    <StatusFormField />
                    <ComboboxFormField
                      title="Priority"
                      fieldName="priority"
                      options={PRIORITY_OPTIONS}
                    />
                  </div>
                  <div className="tw:mt-3 tw:flex tw:flex-col tw:gap-2">
                    <Label htmlFor="actionDescription-textarea">
                      Description
                    </Label>
                    <Textarea
                      id="actionDescription-textarea"
                      placeholder="Optional detail: what done looks like, constraints, references."
                      {...form.register('actionDescription')}
                    />
                  </div>
                  <ResourceRequirements />
                  <HazardsRisks />
                  <ActionAttachments />
                  <div className="mt-3">
                    <FormSymbologySelect
                      name="actionSymbology"
                      label="Select Symbol"
                    />
                    <span className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                      The symbol this task draws with on the map. Leave as None
                      to keep the status and priority marker.
                    </span>
                  </div>
                  <div className="mt-3">
                    <span className="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
                      Location
                    </span>
                    <FormGeoLocationField name="actionLocation" label="" />
                  </div>

                  <ActionChecklist
                    checklist={checklist}
                    setChecklist={setChecklist}
                    editable={!isEdit}
                  />
                </div>

                <div className="rounded border border-neutral-500 p-3">
                  <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
                    Details
                  </div>
                  <div className="tw:flex tw:flex-col tw:gap-3 mt-2">
                    <ComboboxFormField
                      title="Assignee"
                      fieldName="poc"
                      options={adapter.pocOptions}
                    />
                    <ComboboxFormField
                      title="AORs"
                      fieldName="actionAors"
                      options={aorOptions}
                      multiple
                    />
                    <DateTimeFormField
                      title="Scheduled Start"
                      fieldName="start_date"
                    />
                    <DateTimeFormField
                      title="Scheduled End"
                      fieldName="target_date"
                    />
                    <Row>
                      <FormCheckboxField
                        title="Briefed Assignee"
                        fieldName="briefed_poc"
                        variant="full"
                      />
                    </Row>
                    <ComboboxFormField
                      title="Blocked by"
                      fieldName="blockedBy"
                      options={blockedByOptions}
                      multiple
                    />
                    <span className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                      This task stays blocked until every task listed here is
                      Closed.
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              {isEdit && (
                <Button
                  type="button"
                  variant="destructive-outline"
                  onClick={handleDeactivateClick}
                >
                  {selectedEntity?.archived ? 'Activate' : 'Deactivate'}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={toggle}
                data-testid="org-action-close-button"
              >
                Close
              </Button>
              <Button
                type="submit"
                disabled={(!isEdit && !watchedName) || adapter.isSaving}
              >
                Save
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>

        <AlertDialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Deactivation</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to deactivate this action? Deactivated
                actions will be hidden from the active actions tracker but
                preserved in the system for record-keeping.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                disabled={adapter.isSaving}
                onClick={toggleActive}
              >
                Deactivate
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

export default EditOrgActionsModal;
